









































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Mutation, State, Getter, Action } from 'vuex-class';
import {
  IProject,
  IUser,
  ILabor,
  ICRUDResponse,
  IEmployee,
} from '../../models';
import { DateTime, Interval } from 'luxon';
import { client } from '../../store';
import { Watch } from 'vue-property-decorator';
import filter from '@/directives/filter';
import { Route } from 'vue-router';

interface IDateKey {
  userID: number;
  projectID: string;
  date: DateTime;
}
interface IWeekdays {
  isToday: boolean;
  isWeekend: boolean;
  day: number;
  monthShort: string;
  dateTime: DateTime;
}
function tokenize({ userID, projectID, date }: IDateKey) {
  return [userID, projectID, date.toISODate()].join('/');
}
const makeProjectId = (name: string, type: string) => `${name} (${type})`;
function expandLaborTypes(
  projects: IProject[],
  weekDays: IWeekdays[],
  userId: number,
) {
  return projects.flatMap((project) =>
    project.project_labor_types.map((type) => {
      const name = makeProjectId(project.name, type.title);
      return {
        ...project,
        type,
        name,
        days: weekDays.map((day) => ({
          ...day,
          key: tokenize({
            date: day.dateTime,
            userID: userId,
            projectID: name,
          }),
        })),
      };
    }),
  );
}
@Component
export default class LaborTree extends Vue {
  @Action('getLaborByUser', { namespace: 'labor' }) getLaborByUser: any;
  @Action('createLabor', { namespace: 'labor' }) createLabor: any;
  @State('currentUser', { namespace: 'user' }) user: IUser;
  projects: IProject[] = [];
  labor: Record<string, string> = {};
  status: Record<string, undefined | 'error' | 'success'> = {};
  projectAutocomplete: IProject[] = [];
  userAutocomplete: IEmployee[] = [];
  searchString = '';
  userSearch = '';
  isLoading = false;
  isDatePickerOpen = false;
  datePicker = '';
  isUserPickerOpen = false;
  beforeMount() {
    const year = DateTime.local().weekYear.toString();
    const week = DateTime.local().weekNumber.toString();
    if (!this.$route.params.week && !this.$route.params.year) {
      this.$router.replace({ name: 'LaborTree', params: { year, week } });
    }
  }
  beforeUpdate() {
    const year = DateTime.local().weekYear.toString();
    const week = DateTime.local().weekNumber.toString();
    if (!this.$route.params.week && !this.$route.params.year) {
      this.$router.replace({ name: 'LaborTree', params: { year, week } });
    }
  }
  mounted() {
    this.syncLabor();
  }
  get isCurrentUser() {
    return (
      !this.$route.params.userId ||
      this.user.id === Number(this.$route.params.userId)
    );
  }
  updateLabor(item: any, day: any, value: string) {
    const entity: ILabor = {
      date: day.dateTime.toSQL({ includeOffset: false }),
      project_id: item.id,
      project_labor_type_id: item.type.id,
      user_id: this.user.id,
      value: Number(value),
    };
    this.createLabor(entity)
      .then(() => {
        Vue.set(this.status, day.key, 'success');
        setTimeout(() => {
          Vue.set(this.status, day.key, undefined);
        }, 1000);
      })
      .catch(() => Vue.set(this.status, day.key, 'error'));
  }
  get projectTrackableItems() {
    return expandLaborTypes(this.projects, this.weekDays, this.user.id);
  }
  get weeklySum() {
    return this.projectTrackableItems.reduce(
      (acc, project) => {
        return project.days.map(({ key }, idx) => {
          const labor = Number(this.labor[key]) || 0;
          return acc[idx] + labor;
        });
      },
      this.weekDays.map((_) => 0),
    );
  }
  @Watch('$route.params')
  async syncLabor() {
    this.isLoading = true;
    const { year, week, userId } = this.$route.params;
    await Promise.all([
      client
        .get<IProject[]>(`labor/project/user/${userId || this.user.id}`, {
          params: { year, week },
        })
        .then(({ data }) => (this.projects = data)),
      client
        .get(`labor/user/${userId || this.user.id}`, { params: { year, week } })
        .then(({ data }) => {
          (data || []).forEach((labor: ILabor) => {
            Vue.set(
              this.labor,
              tokenize({
                userID: this.user.id,
                projectID: `${labor.project!.name} (${
                  labor.project_labor_type!.title
                })`,
                date: DateTime.fromSQL(labor.date),
              }),
              labor.value,
            );
          });
        }),
    ]);
    this.isLoading = false;
  }
  addProject(project: IProject) {
    this.projects.push(project);
    this.searchString = '';
    this.projectAutocomplete = [];
  }
  @Watch('searchString')
  findProject(name: string = '') {
    if (!name || name.length < 2) return;
    client
      .get('/project/find', { params: { name } })
      .then(({ data }) => (this.projectAutocomplete = data));
  }
  @Watch('userSearch')
  findUser(name: string = '') {
    if (!name || name.length < 2) return;
    client
      .get<IEmployee[]>('user/find', {
        params: { search: name },
      })
      .then(({ data }) => (this.userAutocomplete = data));
  }

  // routing
  modifyRoute(params: object, push?: boolean) {
    const route = { ...this.$route };
    route.params = {
      ...route.params,
      ...params,
    };
    if (push) {
      // @ts-ignore
      this.$router.push(route).catch((_) => {
        return;
      });
    }
    return route;
  }
  jumpToWeek(date: string) {
    const dt = DateTime.fromSQL(date);
    this.modifyRoute(
      {
        year: dt.weekYear,
        week: dt.weekNumber,
      },
      true,
    );
  }
  switchUser(employee: IEmployee) {
    this.modifyRoute({ userId: String(employee.id) }, true);
  }
  get prevWeekLink() {
    const baseDate = this.weekDays[0];
    const date = baseDate.dateTime.minus({ week: 1 });
    const { name, params } = this.modifyRoute({
      year: date.weekYear,
      week: date.weekNumber,
    });
    return { name, params };
  }
  get nextWeekLink() {
    const baseDate = this.weekDays[6];
    const date = baseDate.dateTime.plus({ week: 1 });
    const { name, params } = this.modifyRoute({
      year: date.weekYear,
      week: date.weekNumber,
    });
    return { name, params };
  }
  get weekDays(): Array<{
    isToday: boolean;
    isWeekend: boolean;
    day: number;
    monthShort: string;
    dateTime: DateTime;
  }> {
    const { year, week } = this.$route.params;
    const firstDay = DateTime.fromObject({
      weekYear: Number(year),
      weekNumber: Number(week),
    });
    const lastDay = firstDay.plus({ week: 1 });
    const today = DateTime.local();
    return Interval.fromDateTimes(firstDay, lastDay)
      .splitBy({ day: 1 })
      .map((day, idx) => {
        const startDate = day.start;
        return {
          isToday: startDate.startOf('day').equals(today.startOf('day')),
          isWeekend: startDate.weekday > 5,
          day: startDate.day,
          monthShort: startDate.monthShort,
          dateTime: startDate,
        };
      });
  }
}
